import { createChatPrompt } from "./chatPromptServices"
import { createResponse } from "./cloudFunctions"
import { createChatPromptData } from "./modelEditServices"
import * as palette from "../../components/symbols/palette"
import selectQuestionsSchema from "./schemas/select_questions.json"
import selectContentSchema from "./schemas/select_content.json"
import getContentSchema from "./schemas/get_content.json"

const fillHeading = async ({
  templateInstance,
  viewSet,
  views,
  selectedHeading,
  selectedFileIds,
  selectedViewIds,
  contentGuidance,
}) => {
  // Build view context if any views are selected
  let viewLines = []
  if ((selectedViewIds?.length || 0) > 0) {
    const selectedViews = views.filter((v) =>
      selectedViewIds.find((sel) => sel.id === v.id)
    )

    viewLines = [
      `The views selected to fill the section are as follows; each view begins with an ID attribute:`,
      ...selectedViews.flatMap((view) => {
        const chatPromptData = createChatPromptData({
          currentView: view,
          includeDoc: true,
        })

        const prompts = createChatPrompt({
          promptData: chatPromptData,
          // All layers
          promptLayers: palette.LAYERS.map((layer) => layer.name),
          includeProperties: true,
          includeDoco: false,
          includeIds: false,
        })

        const lines = [
          `ID: ${view.id}`,
          `View: ${view.name}`,
          `Description: ${view.description}`,
          `Importance: ${
            view.importance || "Importance not defined for this view"
          }`,
          `Question: ${view.question || "No question was provided"}`,
          `Summary of view as follows:`,
          ...prompts,
        ]

        if (view.text_response) {
          lines.push(
            `The extra text information accompanying this view that you can use is: ${view.text_response}`
          )
        }

        return [...lines, ""]
      }),
      `That is the end of the list of views selected to fill the section.`,
      "",
    ]
  }

  const lines = [
    `We want to fill the '${selectedHeading.heading}' heading of the '${templateInstance.name}' template using the selected views and/or attached files (if any).`,
    `Purpose of the heading you will populate is: ${selectedHeading.rationale}.`,
    `Overall document context (secondary guidance): ${viewSet.purpose}.`,
    contentGuidance
      ? `Additional content guidance (overrides general style/length advice): ${contentGuidance}.`
      : "",
    `Please review the provided content and, unless further guidance is given on style or length, write a high-quality piece of text appropriate for this section.`,
    `The text should be well structured and coherent, formal in style, and free from spelling and grammatical errors.`,
    `Stay within the bounds of the heading scope (this is one section of a larger document).`,
    `RULE: YOU MUST NOT MAKE UP CONTENT IF IT DOES NOT EXIST IN THE INFORMATION PROVIDED TO YOU. IF YOU DO NOT HAVE ENOUGH INFORMATION TO WRITE A SECTION, YOU MUST SAY SO.`,
    `Just provide the answer; do not include lead-ins like "The answer is..." or any closing explanations.`,
    "",
    ...viewLines,
    "SYSTEM RULES:",
    "- Return ONLY a single JSON object that validates against the provided JSON Schema.",
    "- Only return fields defined by the schema.",
  ].filter(Boolean)

  // Extract vector store IDs from selectedFileIds (supports .vs_id or .vector_store_id)
  const vectorStoreIds = Array.from(
    new Set(
      (selectedFileIds || [])
        .map((f) => f.vs_id || f.vector_store_id)
        .filter(Boolean)
    )
  )

  // Only include file_search tool if we actually have vector store IDs
  const tools =
    vectorStoreIds.length > 0
      ? [{ type: "file_search", vector_store_ids: vectorStoreIds }]
      : undefined

  const resp = await createResponse({
    prompt: lines.join("\n"),
    model: "gpt-5-mini",
    jsonSchema: getContentSchema, // you said to use provided schema directly
    reasoning: { effort: "medium" },
    store: false,
    ...(tools ? { tools } : {}),
  })

  const raw = resp?.response?.output_text
  if (!raw) return null

  const obj = JSON.parse(raw)

  console.log("obj", { obj })

  return obj.content[0].content_item
}

const selectBestViewsToFillSection = async ({
  views,
  viewSet,
  templateInstance,
  selectedHeading,
  contentGuidance,
}) => {
  const lines = [
    `We want to choose which views provide the best and most relevant content to fill the '${selectedHeading.name}' heading of the '${templateInstance.name}' template using selected content in the attached views.`,
    `Heading purpose (primary guidance): ${selectedHeading.rationale}.`,
    `Overall document context (secondary guidance): ${viewSet.purpose}.`,
    `The view content available is as follows and for each view the following fields are provided: id, name, description, importance, question (optional).`,
    `Each view begins with an ID attribute.`,
    "",
    ...views.flatMap((view) => [
      `ID: ${view.id}`,
      `View: ${view.name}`,
      `Description: ${view.description}`,
      `Importance: ${
        view.importance || "Importance not defined for this view"
      }`,
      `Question: ${view.question || "No question was provided"}`,
      "",
    ]),
    `That is the end of the list of views available to fill the section.`,
    `We are seeking to identify the minimum number of relevant views that very closely align to the specified heading purpose.`,
    contentGuidance ? `Additional content guidance: ${contentGuidance}` : "",
    "",
    "SYSTEM RULES:",
    "- Return ONLY a single JSON object that validates against the provided JSON Schema.",
    "- Only return fields defined by the schema.",
  ].filter(Boolean)

  const resp = await createResponse({
    prompt: lines.join("\n"),
    model: "gpt-5-mini",
    // Use the provided schema object directly
    jsonSchema: selectContentSchema,
    reasoning: { effort: "low" },
    store: false,
  })

  const raw = resp?.response?.output_text
  if (!raw) return null

  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw
  } catch {
    // Fallback if the model wraps JSON in text
    const match = String(raw).match(/\{[\s\S]*\}/)
    return match ? JSON.parse(match[0]) : null
  }
}

const selectQuestionsToFillSection = async ({
  views,
  viewSet,
  templateInstance,
  selectedHeading,
  contentGuidance,
}) => {
  const answeredQuestionIds = views.map((v) => v.question_id).filter(Boolean)

  const answeredQuestions = views.filter((v) =>
    answeredQuestionIds.includes(v.question_id)
  )

  const unansweredQuestions = (viewSet.view_questions || []).filter(
    (q) => !answeredQuestionIds.includes(q.question_id)
  )

  const lines = [
    `We want to choose which questions would be good to answer to fill the '${selectedHeading.name}' heading of the '${templateInstance.name}' template.`,
    `Heading purpose (primary guidance): ${selectedHeading.rationale}`,
    `Overall document context (secondary guidance): ${viewSet.purpose}`,
    contentGuidance ? `Additional content guidance: ${contentGuidance}` : "",
    "",
    answeredQuestions.length > 0
      ? `The questions that have already been answered are:`
      : `No questions have been answered yet.`,
    ...answeredQuestions.map((q) => `ID [${q.question_id}]: ${q.question}`),
    "",
    `From the following UNANSWERED questions, select the minimum set that would best fill the '${selectedHeading.name}' heading:`,
    ...unansweredQuestions.map((q) => `ID [${q.question_id}]: ${q.question}`),
    "",
    `For each selected question, provide a rationale explaining why it helps fill the heading.`,
    "",
    "SYSTEM RULES:",
    "- Return ONLY a single JSON object that validates against the provided JSON Schema.",
    "- Only return fields defined by the schema.",
  ].filter(Boolean)

  const resp = await createResponse({
    prompt: lines.join("\n"),
    model: "gpt-5-mini",
    // You said: when you provide a schema, use it directly.
    jsonSchema: selectQuestionsSchema,
    reasoning: { effort: "low" },
    store: false,
  })

  const raw = resp?.response?.output_text

  console.log("%craw", "color:orange", { raw })

  const obj = JSON.parse(raw)

  console.log("%cparsed", "color:lightgreen", { obj })

  return obj
}

export {
  fillHeading,
  selectBestViewsToFillSection,
  selectQuestionsToFillSection,
}
